/**
 * Reference column (TECH-JOB-SCREEN.md §2.2) — the right column of the tech
 * job screen: everything the tech looks up but never acts on. Every fact
 * renders exactly once here: briefing scope, amber scheduling notes, the
 * device list with live status chips, the location/purveyor card, Job Messages
 * as a single row, and the Manual PDF escape hatch.
 *
 * Files do NOT live here. Photos and report PDFs used to be two more cards at
 * the bottom of this stack, which meant the job's paperwork was split across
 * five cards on the longest column of the screen — and the original work order
 * wasn't in any of them. They now live in one JobFilesCard pinned above this
 * column (Issue #719).
 */
import { lazy, Suspense } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { openExternalLink } from "@/lib/standalone";
import { assetVisual } from "@/lib/asset-visual";
import { cn, formatDateTime } from "@/lib/utils";
import { ChevronRight, Droplet, MapPin, MessageSquare, PenLine, Pin, Plus, User } from "lucide-react";
import type { DeviceChipStatus } from "./steps";
import { PortalAccountCard } from "./portal-account-card";
import "@uiw/react-md-editor/markdown-editor.css";
import { scopePlainWords } from "../redesign-copy";

// The office summary is authored as Markdown; render it (bullet lists, bold,
// italics) with the same renderer the office notes use. Lazy-loaded so the
// md-editor stays out of the field chunk until a job actually carries one.
const SummaryMarkdown = lazy(() => import("@uiw/react-md-editor").then((m) => ({ default: m.default.Markdown })));

export interface ReferenceBriefing {
    workOrderNumber: string | null;
    scopeOfWork: string | null;
    specialInstructions: string | null;
    trackingNumber: string | null;
    siteContactName: string | null;
    siteContactPhone: string | null;
}

export interface ReferenceDevice {
    linkId: string;
    assetId: string;
    label: string;
    subline: string | null;
    serial: string | null;
    location: string | null;
    assetType: string | null;
    requiredAction: string | null;
    status: DeviceChipStatus;
}

export interface ReferenceLocation {
    siteName: string | null;
    address: string | null;
    contactName: string | null;
    contactRole: string | null;
    providerName: string | null;
    providerPlatform: string | null;
    providerIsPortal: boolean | null;
    providerPortalUrl: string | null;
    accountNumber: string | null;
}

export interface ReferenceNote {
    id: string;
    authorName: string | null;
    body: string;
    pinned: boolean;
    createdAt: string;
}

export interface ReferenceColumnProps {
    briefing: ReferenceBriefing | null;
    /** Office-authored on-site brief (jobs.summary, Markdown) — pinned to the
     *  top of the reference column. The office writes it; the tech reads it. */
    summary?: string | null;
    /** Amber scheduling / tech notes — always visible (spec §2.2). */
    warningText: string | null;
    /** Non-WO jobs: office notes for the tech (single home). */
    techNotes: string | null;
    /** Job notes thread (newest first, pinned on top). When non-empty it
     *  replaces the single techNotes string in the brief. */
    notesThread?: ReferenceNote[];
    /** Tech "Add note" affordance. When provided, the Notes card always
     *  renders (even with an empty thread) with an Add note button so a tech
     *  can jot a note any time during the job — not only at closeout. Null
     *  hides the button (office/read-only contexts, existing tests). */
    onAddNote?: (() => void) | null;
    devices: ReferenceDevice[];
    onDeviceTap: ((assetId: string) => void) | null;
    location: ReferenceLocation;
    /** #329 — needed only to offer the portal-account card when the site's
     *  online jurisdiction has no account # yet. Optional so existing
     *  office/read-only mounts and tests render exactly as before. */
    jobId?: string | null;
    unreadMessages: number;
    onOpenMessages: () => void;
    /** Manual PDF escape hatch (vendor WOs). Null hides the row. */
    onManualPdf: (() => void) | null;
    /** #948 J3 — plain-words scope behind `field_redesign_v2`. */
    redesign?: boolean;
    /** `required_action` per device, for what today's visit actually is. */
    scopeActions?: Array<string | null | undefined>;
    scopeDeviceCount?: number;
    /** Add from the field: under each device row, its past-visit notes and its
     *  own small +. Null/absent renders the rows exactly as before. */
    renderDeviceExtra?: ((assetId: string) => React.ReactNode) | null;
}

const CHIP: Record<DeviceChipStatus, { label: string; className: string }> = {
    pass: { label: "PASS", className: "bg-green-700 text-white" },
    fail: { label: "FAIL", className: "bg-destructive text-destructive-foreground" },
    skipped: { label: "SKIPPED", className: "bg-amber-500/20 text-amber-700 dark:text-amber-400" },
    not_tested: { label: "NOT TESTED", className: "bg-secondary text-foreground/70" },
    // Helper Mode v2 honest state: the helper entered readings — the device
    // is waiting on the tech to review & sign, never "complete".
    entered: { label: "ENTERED", className: "bg-amber-500/20 text-amber-700 dark:text-amber-400" },
};

function SectionLabel({ children }: { children: React.ReactNode }) {
    return <div className="text-xs font-semibold uppercase tracking-wider text-foreground/70">{children}</div>;
}

export function ReferenceColumn({
    briefing,
    summary,
    warningText,
    techNotes,
    notesThread = [],
    onAddNote = null,
    devices,
    onDeviceTap,
    location,
    jobId = null,
    unreadMessages,
    onOpenMessages,
    redesign,
    scopeActions,
    scopeDeviceCount,
    onManualPdf,
    renderDeviceExtra = null,
}: ReferenceColumnProps) {
    const colorMode =
        typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? "dark" : "light";
    return (
        <div className="space-y-3">
            {/* ── Summary — the office's on-site brief, pinned to the TOP of
                the reference column (office authors it; the tech reads it).
                Rich text so bullet lists / bold / italics render. ── */}
            {summary?.trim() && (
                <Card className="border-l-4 border-l-amber-500/80 dark:border-l-amber-400/80">
                    <CardContent className="space-y-2 p-4">
                        <SectionLabel>Summary</SectionLabel>
                        <div data-color-mode={colorMode}>
                            <Suspense
                                fallback={<div className="text-sm leading-snug whitespace-pre-line">{summary}</div>}
                            >
                                <SummaryMarkdown
                                    source={summary}
                                    style={{ background: "transparent", fontSize: "0.875rem" }}
                                />
                            </Suspense>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* ── Work order briefing — scope renders ONCE, here ── */}
            {briefing && (briefing.scopeOfWork || briefing.specialInstructions || briefing.workOrderNumber) && (
                <Card className="border-primary/40">
                    <CardContent className="space-y-3 p-4">
                        <div className="flex items-center justify-between">
                            <SectionLabel>Work Order Briefing</SectionLabel>
                            {briefing.workOrderNumber && (
                                <span className="font-mono text-xs text-foreground/70">
                                    WO {briefing.workOrderNumber}
                                </span>
                            )}
                        </div>
                        {redesign
                            ? /* J3 — what TODAY is, then the boundary, then the
                               office's own words demoted to a labeled note.
                               The office's text often describes a whole
                               relationship ("testing and repair as needed")
                               while today's job is one test; a green tech
                               reads that as licence to pull a valve apart. */
                              (() => {
                                  const scope = scopePlainWords({
                                      scopeOfWork: briefing.scopeOfWork,
                                      actions: scopeActions ?? [],
                                      deviceCount: scopeDeviceCount ?? 0,
                                  });
                                  return (
                                      <div className="space-y-2" data-testid="scope-plain-words">
                                          <p className="text-sm font-semibold leading-snug">{scope.doing}</p>
                                          {scope.notDoing && (
                                              <p className="text-sm leading-snug" data-testid="scope-not-doing">
                                                  {scope.notDoing}
                                              </p>
                                          )}
                                          {scope.conflict && (
                                              <p
                                                  className="rounded-sm border-l-2 border-amber-500 bg-amber-50 py-2 pl-3 text-sm leading-snug dark:bg-amber-500/10"
                                                  data-testid="scope-conflict"
                                              >
                                                  {scope.conflict}
                                              </p>
                                          )}
                                          {scope.officeWording && (
                                              <details data-testid="scope-office-wording">
                                                  <summary className="cursor-pointer text-xs font-medium text-primary">
                                                      What the office wrote
                                                  </summary>
                                                  <div className="mt-1 whitespace-pre-line text-sm leading-snug text-foreground/80">
                                                      {scope.officeWording}
                                                  </div>
                                              </details>
                                          )}
                                      </div>
                                  );
                              })()
                            : briefing.scopeOfWork && (
                                  <div className="text-sm leading-snug whitespace-pre-line">{briefing.scopeOfWork}</div>
                              )}
                        {briefing.specialInstructions && (
                            <div className="rounded-sm border-l-2 border-amber-500 bg-amber-50 py-2 pl-3 dark:bg-amber-500/10">
                                <div className="mb-0.5 text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                                    Special instructions
                                </div>
                                <div className="text-sm leading-snug whitespace-pre-line">
                                    {briefing.specialInstructions}
                                </div>
                            </div>
                        )}
                        {(briefing.siteContactName || briefing.siteContactPhone) && (
                            <div className="flex items-center gap-2 text-sm">
                                <User className="h-4 w-4 shrink-0 text-foreground/70" />
                                {briefing.siteContactName && <span>{briefing.siteContactName}</span>}
                                {briefing.siteContactPhone && (
                                    <a
                                        href={`tel:${briefing.siteContactPhone.replace(/[^\d+]/g, "")}`}
                                        className="text-primary underline"
                                    >
                                        {briefing.siteContactPhone}
                                    </a>
                                )}
                            </div>
                        )}
                        {briefing.trackingNumber && (
                            <div className="text-xs text-foreground/70">Tracking #: {briefing.trackingNumber}</div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* ── Amber "Notes for Tech" — always visible ──
                Opaque surface required: the BlueprintBackdrop watermark
                renders behind everything, so a translucent tint washes out
                (AGENTS.md frontend rule). */}
            {warningText && (
                <div className="rounded-lg border border-amber-400 bg-card p-3 dark:border-amber-500/70">
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                        Notes for Tech
                    </div>
                    <div className="text-sm leading-snug whitespace-pre-line">{warningText}</div>
                </div>
            )}

            {/* ── Job notes thread (append-only, newest first, pinned on top).
                Falls back to the single legacy string when the thread is empty
                (e.g. tech_instructions-only jobs). With onAddNote the card
                always renders — a tech can jot a note any time during the job
                (not only at closeout) via the Add note button. ── */}
            {(notesThread.length > 0 || techNotes || onAddNote) && (
                <Card>
                    <CardContent className="space-y-2 p-4">
                        <div className="flex min-h-[32px] items-center justify-between gap-2">
                            <SectionLabel>
                                {notesThread.length > 0 ? `Notes (${notesThread.length})` : "Notes for Tech"}
                            </SectionLabel>
                            {onAddNote && (
                                <Button variant="outline" size="sm" className="h-9" onClick={onAddNote}>
                                    <Plus className="mr-1.5 h-4 w-4" /> Add note
                                </Button>
                            )}
                        </div>
                        {notesThread.length > 0 ? (
                            <div className="divide-y">
                                {notesThread.map((n) => (
                                    <div key={n.id} className="space-y-0.5 py-2 first:pt-0 last:pb-0">
                                        <div className="flex items-center gap-1.5 text-[11px] text-foreground/70">
                                            {n.pinned && (
                                                <Pin className="h-3 w-3 shrink-0 text-amber-600 dark:text-amber-400" />
                                            )}
                                            <span className="font-semibold">{n.authorName ?? "System"}</span>
                                            <span>· {formatDateTime(n.createdAt)}</span>
                                        </div>
                                        <div className="text-sm leading-snug whitespace-pre-line">{n.body}</div>
                                    </div>
                                ))}
                            </div>
                        ) : techNotes ? (
                            <div className="text-sm leading-snug whitespace-pre-line">{techNotes}</div>
                        ) : (
                            <p className="text-xs text-foreground/60">
                                No notes yet — add anything the office should know while you're on the job.
                            </p>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* ── Devices — ONE list, live status chips ── */}
            {devices.length > 0 && (
                <Card>
                    <CardContent className="space-y-2 p-4">
                        <SectionLabel>Devices ({devices.length})</SectionLabel>
                        <div className="divide-y">
                            {devices.map((d) => {
                                const visual = assetVisual(d.assetType);
                                const VisualIcon = visual.Icon;
                                const chip = CHIP[d.status];
                                const row = (
                                    <div className="flex min-h-[44px] items-start gap-2.5 py-2">
                                        <div
                                            className={cn(
                                                "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                                                visual.badgeClassName,
                                            )}
                                        >
                                            <VisualIcon className="h-4 w-4" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-sm font-medium leading-tight">{d.label}</div>
                                            {d.subline && (
                                                <div className="truncate text-xs text-foreground/70">{d.subline}</div>
                                            )}
                                            {d.location && (
                                                <div className="flex items-center gap-1 text-xs text-foreground/70">
                                                    <MapPin className="h-3 w-3 shrink-0" />
                                                    <span className="truncate">{d.location}</span>
                                                </div>
                                            )}
                                        </div>
                                        <Badge
                                            className={cn(
                                                "shrink-0 border-transparent text-[10px] font-bold tracking-wide",
                                                d.status === "not_tested" && d.requiredAction === "repair"
                                                    ? "bg-amber-100 text-amber-900"
                                                    : d.status === "not_tested" && d.requiredAction === "replace"
                                                      ? "bg-blue-100 text-blue-900"
                                                      : chip.className,
                                            )}
                                        >
                                            {/* Untouched devices say what today does to them:
                                                a repair reads REPAIR, never "NOT TESTED". */}
                                            {d.requiredAction === "replace" && d.status === "not_tested"
                                                ? "REPLACE"
                                                : d.requiredAction === "repair" && d.status === "not_tested"
                                                  ? "REPAIR"
                                                  : chip.label}
                                        </Badge>
                                    </div>
                                );
                                const extra = renderDeviceExtra ? renderDeviceExtra(d.assetId) : null;
                                return onDeviceTap ? (
                                    <div key={d.linkId}>
                                        <button
                                            type="button"
                                            className="block w-full text-left"
                                            onClick={() => onDeviceTap(d.assetId)}
                                        >
                                            {row}
                                        </button>
                                        {extra}
                                    </div>
                                ) : (
                                    <div key={d.linkId}>
                                        {row}
                                        {extra}
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* ── Location + purveyor (bold treatment, office-page parity) ── */}
            <Card>
                <CardContent className="space-y-2.5 p-4">
                    <SectionLabel>Location</SectionLabel>
                    <div>
                        <div className="font-medium leading-snug">{location.siteName || "—"}</div>
                        {location.address && (
                            <div className="text-sm leading-snug text-foreground/80 whitespace-pre-line">
                                {location.address}
                            </div>
                        )}
                    </div>
                    {location.contactName && (
                        <div className="flex items-center gap-2 text-sm">
                            <User className="h-4 w-4 shrink-0 text-foreground/70" />
                            <span className="font-medium">{location.contactName}</span>
                            {location.contactRole && (
                                <Badge variant="secondary" className="text-[10px]">
                                    {location.contactRole}
                                </Badge>
                            )}
                        </div>
                    )}
                    {location.providerName && (
                        <div className="rounded-md border border-l-4 border-l-sky-500 bg-sky-50/50 p-2.5 dark:border-l-sky-400 dark:bg-sky-500/10">
                            <div className="flex items-center gap-1.5">
                                <Droplet className="h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" />
                                <span className="text-sm font-bold leading-tight">{location.providerName}</span>
                            </div>
                            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                                <Badge variant="outline" className="text-[10px] font-semibold">
                                    {location.providerPlatform
                                        ? `${location.providerIsPortal ? "Portal" : "Reports"}: ${location.providerPlatform}`
                                        : "Reports: Paper / manual"}
                                </Badge>
                                {/* Portal-only: paper/offline jurisdictions have no portal
                                    account, so a stored number is stale/legacy — never show it. */}
                                {location.providerIsPortal && location.accountNumber && (
                                    <span className="font-mono font-semibold select-all">
                                        Acct #{location.accountNumber}
                                    </span>
                                )}
                                {location.providerPortalUrl && (
                                    <button
                                        type="button"
                                        onClick={() => openExternalLink(location.providerPortalUrl!)}
                                        className="font-medium text-primary hover:underline"
                                    >
                                        Open portal
                                    </button>
                                )}
                            </div>
                            {/* #329 — online jurisdiction, no account # on file. This
                                used to be the state that hard-blocked "On My Way" with
                                a 409 pointing at an office-only card. Now the job runs
                                and the tech gets an optional way to help. */}
                            {location.providerIsPortal && !location.accountNumber && jobId && (
                                <PortalAccountCard jobId={jobId} platform={location.providerPlatform} />
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ── Job messages — one row → with unread badge ── */}
            <Card className="cursor-pointer transition-colors hover:bg-secondary/30" onClick={onOpenMessages}>
                <CardContent className="flex min-h-[44px] items-center gap-3 p-4">
                    <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <MessageSquare className="h-4 w-4" />
                        {unreadMessages > 0 && (
                            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                                {unreadMessages > 9 ? "9+" : unreadMessages}
                            </span>
                        )}
                    </div>
                    <div className="flex-1">
                        <div className="text-sm font-medium">Job Messages</div>
                        <div className="text-xs text-foreground/70">
                            {unreadMessages > 0
                                ? `${unreadMessages} unread message${unreadMessages === 1 ? "" : "s"}`
                                : "Message the office about this job"}
                        </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-foreground/70" />
                </CardContent>
            </Card>

            {/* ── Manual PDF — escape hatch at the bottom ── */}
            {onManualPdf && (
                <Button variant="outline" className="h-12 w-full" onClick={onManualPdf}>
                    <PenLine className="mr-2 h-4 w-4" /> Manual PDF
                </Button>
            )}
        </div>
    );
}
